/**
 * The one place that decides whether a request carries X-Org-ID (design 01 4.1, 6.5).
 *
 * Rules:
 * - Only inside a business workspace (an org is selected and the feature is on).
 * - Only for the chats routes: list and create (`/v1/chats`), get, update and delete
 *   (`/v1/chats/{id}`) and messages (`/v1/chats/{id}/messages`).
 * - The business data routes (`/v1/business/...`, design 02 section 5): the public routes
 *   read the org from this header, so every path under that prefix carries it in a
 *   business workspace. Never in Personal, never with the feature off.
 * - The org routes (`/v1/orgs/...`, `/v1/org-invitations/...`) carry the org in the path
 *   and must NOT send the header (the server answers 400 org_mismatch on a mismatch).
 * - Chat completions send it exactly when a business workspace is active (every chat
 *   there is an org chat; a workspace switch resets chat state). Personal workspace and
 *   the flag off never send it. If a workspace is selected but no usable org id can be
 *   built, completions fail closed (see completionsOrgGate) instead of becoming a
 *   personal turn.
 * Everything else, including every personal request, is untouched.
 */
import { getActiveOrgId } from './workspaceStore';

export const ORG_ID_HEADER = 'X-Org-ID';

const CHAT_ROUTES: readonly RegExp[] = [
  /^\/v1\/chats$/,
  /^\/v1\/chats\/[^/]+$/,
  /^\/v1\/chats\/[^/]+\/messages$/,
  /^\/v1\/chats\/[^/]+\/messages\/window$/,
  /^\/v1\/chats\/[^/]+\/read$/,
];

/** `/v1/business` and everything below it. */
const BUSINESS_ROUTE = /^\/v1\/business(?:\/.*)?$/;

function pathOf(url: string | undefined): string {
  if (!url) return '';
  const noQuery = url.split('?')[0] ?? '';
  return noQuery.length > 1 ? noQuery.replace(/\/+$/, '') : noQuery;
}

/**
 * The main chat is a PERSONAL surface (design 01 6.4): approval cards land there and it
 * is never an org chat. Whatever targets it sends no org header, in any workspace. Its id
 * is registered when `GET /v1/main-thread` answers (the page always loads that first).
 */
let mainChatId: string | null = null;

export function registerMainChatId(id: string | null | undefined): void {
  mainChatId = typeof id === 'string' && id.length > 0 ? id : null;
}

function isMainChat(chatId: string | null | undefined): boolean {
  return mainChatId !== null && chatId === mainChatId;
}

function chatIdOfPath(path: string): string | null {
  const match = /^\/v1\/chats\/([^/]+)(?:\/|$)/.exec(path);
  return match?.[1] ?? null;
}

/** Org id for an axios request URL (relative to the API base), or null for none. */
export function orgIdForApiUrl(url: string | undefined): string | null {
  const orgId = getActiveOrgId();
  if (!orgId) return null;
  const path = pathOf(url);
  // A dot segment (or an encoded one) can make a /v1/business URL resolve to a different API path: never tag it.
  if (/(?:^|\/)(?:\.|%2e){1,2}(?:\/|$)/i.test(path) || /%2f|%5c|\\/i.test(path)) return null;
  if (isMainChat(chatIdOfPath(path))) return null;
  if (BUSINESS_ROUTE.test(path)) return orgId;
  return CHAT_ROUTES.some((route) => route.test(path)) ? orgId : null;
}

const ORG_ID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type CompletionsOrgGate =
  | { kind: 'personal' }
  | { kind: 'org'; orgId: string }
  | { kind: 'blocked' };

/**
 * Decides the org header for a chat completion.
 * - personal: no business workspace (or the feature is off), or the chat is the main
 *   chat (always personal): send no X-Org-ID.
 * - org: business workspace active: send X-Org-ID with this id.
 * - blocked: a workspace is selected but its id is unusable. Never send such a turn
 *   without the header (it would run as a personal turn); the caller refuses to send.
 */
export function completionsOrgGate(chatId?: string | null): CompletionsOrgGate {
  const orgId = getActiveOrgId();
  if (orgId === null) return { kind: 'personal' };
  if (isMainChat(chatId)) return { kind: 'personal' };
  return ORG_ID_SHAPE.test(orgId) ? { kind: 'org', orgId } : { kind: 'blocked' };
}
